import { getVariantDisplayQuantity } from '../../../utils/variant-display';
import { CategoryFilterComponent } from '../../../Design/category-filter.component';
import { CatalogMerchandisingComponent } from './catalog-merchandising.component';
import { CategoryFilterSelection } from '../../../utils/category-filter';
import { CommonModule } from '@angular/common';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { AdminPaginatorComponent } from '../../../Design/admin-paginator.component';
import { StockReasonComponent } from '../../../Design/stock-reason.component';
import { supabase } from '../../../core/supabase/supabase.client';
import { SnackbarService } from '../../../services/design/snackbar.service';
import { StoreProductService } from '../../../services/store-product/store-product.service';
import { AuthService } from '../../../services/auth/auth.service';
import {
  StoreService,
  type StoreOption,
} from '../../../services/stores/store.service';
import {
  deriveInventoryStatus,
  type InventoryStatus,
} from '../../../utils/inventory-status';

interface Store {
  id: number;
  name: string;
  store_code?: string;
  city?: string;
  is_active?: boolean;
}

interface Variant {
  storeProductId: number;
  id: number;
  name: string;
  quantity: number;
  unit: string;
  mrp: number;
  price: number;
  stock: number;
  isAvailable: boolean;
  status: InventoryStatus;
  lowStockThreshold: number;
  maxOrderQuantity?: number | null;
}

interface StoreProduct {
  groupKey: string;
  storeId: number;
  storeName: string;
  id: number;
  name: string;
  category: string;
  image: string;
  variants: Variant[];
}

type VariantStatus = InventoryStatus;
type SortField = 'name' | 'stock' | 'variants';
type SortOrder = 'asc' | 'desc';
type QuickFilter = 'all' | 'IN STOCK' | 'LOW STOCK' | 'OUT OF STOCK' | 'UNAVAILABLE';
type StockEditMode = 'add' | 'set';

interface StockEditDraft {
  storeProductId: number;
  productId: number;
  productName: string;
  variantId: number;
  variantName: string;
  currentStock: number;
  mode: StockEditMode;
  quantity: number;
  lowStockThreshold: number;
  isAvailable: boolean;
}

interface VariantEditDraft {
  storeProductId: number;
  productId: number;
  productName: string;
  variantId: number;
  variantName: string;
  mrp: number;
  sellingPrice: number;
  maxOrderQuantity?: number | null;
  isAvailable: boolean;
}

interface VariantRemoveDraft {
  storeId: number;
  storeProductId: number;
  productId: number;
  productName: string;
  variantId: number;
  variantName: string;
}
@Component({
  selector: 'app-store-product',
  imports: [CommonModule, RouterLink, FormsModule, AdminPaginatorComponent, CategoryFilterComponent, CatalogMerchandisingComponent, StockReasonComponent],
  templateUrl: './store-product.component.html',
  styleUrls: ['./store-product.component.scss'],
})
export class StoreProductsComponent implements OnInit {
  protected readonly Math = Math;
  private StoreProductService = inject(StoreProductService);
  private readonly storeService = inject(StoreService);
  private readonly snackbar = inject(SnackbarService);
  readonly authService = inject(AuthService);

  stores = signal<Store[]>([]);
  selectedStoreId = signal<number | null>(null);
  loading = signal(false);
  error = signal<string | null>(null);

  searchText = signal('');
  categoryFilter = signal<CategoryFilterSelection>({ id: null, ids: [] });

  setCategory(selection: CategoryFilterSelection): void {
    this.categoryFilter.set(selection);
    this.expandedProducts.set(new Set());
    this.loadStoreProducts();
  }

  availability = signal<QuickFilter>('all');
  sortField = signal<SortField>('name');
  sortOrder = signal<SortOrder>('asc');

  currentPage = signal(1);
  pageSize = signal(10);
  totalStoreProductRows = signal(0);

  storeProducts = signal<StoreProduct[]>([]);
  expandedProducts = signal<Set<string>>(new Set());
  private loadSequence = 0;

  stockEditDraft = signal<StockEditDraft | null>(null);
  stockSaving = signal(false);
  stockChangeReason = '';

  variantEditDraft = signal<VariantEditDraft | null>(null);
  variantSaving = signal(false);

  variantRemoveDraft = signal<VariantRemoveDraft | null>(null);
  variantRemoveSaving = signal(false);
  variantRemoveError = signal('');

  filteredProducts = computed(() => this.storeProducts());

  paginatedProducts = computed(() => {
    return this.filteredProducts();
  });

  totalProducts = computed(() => this.totalStoreProductRows());
  totalPages = computed(() =>
    Math.ceil(this.totalProducts() / this.pageSize())
  );

  totalVariants = computed(() =>
    this.filteredProducts().reduce((sum, p) => sum + p.variants.length, 0)
  );

  liveVariants = computed(() =>
    this.filteredProducts().reduce(
      (sum, p) =>
        sum + p.variants.filter((v) => v.status === 'IN STOCK').length,
      0
    )
  );

  lowStockVariants = computed(() =>
    this.filteredProducts().reduce(
      (sum, p) => sum + p.variants.filter((v) => v.status === 'LOW STOCK').length,
      0
    )
  );

  outOfStockVariants = computed(() =>
    this.filteredProducts().reduce(
      (sum, p) => sum + p.variants.filter((v) => v.status === 'OUT OF STOCK').length,
      0
    )
  );

  totalStockUnits = computed(() =>
    this.filteredProducts().reduce(
      (sum, p) => sum + p.variants.reduce((s, v) => s + v.stock, 0),
      0
    )
  );

  selectedStoreName = computed(() => {
    const id = this.selectedStoreId();
    return this.stores().find((s) => s.id === id)?.name ?? 'All mapped stores';
  });

  quickFilters = computed(() => [
    { label: 'All', value: 'all' as const, count: this.totalProducts() },
    {
      label: 'In Stock',
      value: 'IN STOCK' as const,
      count: this.liveVariants(),
    },
    {
      label: 'Low Stock',
      value: 'LOW STOCK' as const,
      count: this.lowStockVariants(),
    },
    {
      label: 'Out of Stock',
      value: 'OUT OF STOCK' as const,
      count: this.outOfStockVariants(),
    },
    {
      label: 'Unavailable',
      value: 'UNAVAILABLE' as const,
      count: this.filteredProducts().reduce(
        (sum, p) =>
          sum + p.variants.filter((v) => v.status === 'UNAVAILABLE').length,
        0
      ),
    },
  ]);

  ngOnInit(): void {
    const role = this.authService.role();
    const assignedStoreId = this.authService.storeId();
    if (role === 'store_manager' && assignedStoreId) {
      this.selectedStoreId.set(Number(assignedStoreId));
    }
    this.loadStores();
    this.loadStoreProducts();
  }

  loadStores(): void {
    this.storeService.allStores().subscribe({
      next: (stores: StoreOption[]) => {
        const role = this.authService.role();
        const assignedStoreId = this.authService.storeId();
        if (role === 'store_manager' && assignedStoreId) {
          this.stores.set(
            stores.filter((s: StoreOption) => s.id === Number(assignedStoreId))
          );
          this.selectedStoreId.set(Number(assignedStoreId));
        } else {
          this.stores.set(stores);
        }
      },
      error: (err: unknown) => console.error('Failed to load stores', err),
    });
  }

  loadStoreProducts(resetPage = true): void {
    const sequence = ++this.loadSequence;
    this.loading.set(true);
    this.error.set(null);
    if (resetPage) {
      this.currentPage.set(1);
    }

    const filters = {
      storeId: this.selectedStoreId() || undefined,
      categoryIds: this.categoryFilter().ids,
      search: this.searchText().trim() || undefined,
      page: this.currentPage(),
      pageSize: this.pageSize(),
      availability: this.availability(), sort: this.sortField(), ascending: this.sortOrder() === 'asc',
    };

    this.StoreProductService.getStoreProductGroups(filters).subscribe({
      next: (response) => {
        if (sequence !== this.loadSequence) return;
        const data = response.data as any[];
        this.totalStoreProductRows.set(response.count);
        // Map and group variants by product
        const grouped = data.reduce((acc: StoreProduct[], item: any) => {
          const product = item.variant?.product;
          if (!product) return acc;

          const groupKey = `${item.store_id}:${product.id}`;
          let storeProduct = acc.find((p) => p.groupKey === groupKey);
          if (!storeProduct) {
            const primaryImage =
              product.images?.find((img: any) => img.is_primary) ||
              product.images?.[0];
            let imagePath = 'assets/images/placeholder.png';

            if (primaryImage?.files?.bucket && primaryImage?.files?.path) {
              const { data } = supabase.storage
                .from(primaryImage.files.bucket)
                .getPublicUrl(primaryImage.files.path);
              imagePath = data.publicUrl;
            }

            storeProduct = {
              groupKey, storeId: Number(item.store_id), storeName: item.store?.name || 'Store',
              id: product.id,
              name: product.name,
              category: product.categories?.[0]?.categories?.name || 'General',
              image: imagePath,
              variants: [],
            };
            acc.push(storeProduct);
          }

          storeProduct.variants.push({
            storeProductId: item.id,
            id: item.variant.id,
            name: `${getVariantDisplayQuantity(item.variant)} (${item.variant.sku})`,
            quantity: item.variant.quantity,
            unit: item.variant.unit,
            mrp: item.variant.mrp,
            price: item.price,
            stock: item.stock,
            maxOrderQuantity: item.max_order_quantity ?? null,
            isAvailable: item.is_available,
            lowStockThreshold: item.low_stock_threshold ?? 5,
            status: this.getStatusFromStock(
              item.stock,
              item.is_available,
              item.low_stock_threshold ?? 5
            ),
          });

          return acc;
        }, []);

        this.storeProducts.set(grouped);
        this.loading.set(false);
      },
      error: (err) => {
        if (sequence !== this.loadSequence) return;
        this.error.set('Failed to load store products. Please try again.');
        this.loading.set(false);
        console.error('Failed to load store products', err);
      },
    });
  }

  private getStatusFromStock(
    stock: number,
    isAvailable: boolean,
    threshold = 5
  ): InventoryStatus {
    return deriveInventoryStatus(stock, isAvailable, threshold);
  }

  onStoreChange(): void {
    this.expandedProducts.set(new Set());
    this.variantEditDraft.set(null);
    this.stockEditDraft.set(null);
    this.variantRemoveDraft.set(null);
    this.loadStoreProducts();
  }

  onSearchChange(): void {
    this.loadStoreProducts();
  }

  onAvailabilityChange(): void {
    this.loadStoreProducts();
  }

  setQuickFilter(filter: QuickFilter): void {
    this.availability.set(filter);
    this.loadStoreProducts();
  }

  setSortField(field: SortField): void {
    if (this.sortField() === field) {
      this.sortOrder.set(this.sortOrder() === 'asc' ? 'desc' : 'asc');
    } else {
      this.sortField.set(field);
      this.sortOrder.set('asc');
    }
    this.loadStoreProducts();
  }

  toggleExpandProduct(productId: string): void {
    const current = this.expandedProducts();
    const newSet = new Set(current);
    if (newSet.has(productId)) {
      newSet.delete(productId);
    } else {
      newSet.add(productId);
    }
    this.expandedProducts.set(newSet);
  }

  isExpanded(productId: string): boolean {
    return this.expandedProducts().has(productId);
  }

  setPage(page: number): void {
    if (page >= 1 && page <= this.totalPages()) {
      this.currentPage.set(page);
      this.loadStoreProducts(false);
    }
  }

  setPageSize(pageSize: unknown): void {
    const numericPageSize = Number(pageSize);
    if (!Number.isFinite(numericPageSize) || numericPageSize <= 0) return;
    this.pageSize.set(numericPageSize);
    this.loadStoreProducts();
  }

  refreshInventory(): void {
    this.loadStoreProducts();
  }

  openStockEditor(product: StoreProduct, variant: Variant): void {
    this.stockChangeReason = '';
    if (!this.selectedStoreId()) {
      this.snackbar.warning('Select a store to edit stock');
      return;
    }

    this.stockEditDraft.set({
      storeProductId: variant.storeProductId,
      productId: product.id,
      productName: product.name,
      variantId: variant.id,
      variantName: variant.name,
      currentStock: variant.stock,
      mode: 'add',
      quantity: 0,
      lowStockThreshold: variant.lowStockThreshold,
      isAvailable: variant.isAvailable,
    });
  }

  closeStockEditor(): void {
    if (this.stockSaving()) return;
    this.stockEditDraft.set(null);
  }

  openVariantEditor(product: StoreProduct, variant: Variant): void {
    if (!this.selectedStoreId()) {
      this.snackbar.warning('Select a store to edit');
      return;
    }
    console.log('Opening variant editor for', product, variant);
    this.variantEditDraft.set({
      storeProductId: variant.storeProductId,
      productId: product.id,
      productName: product.name,
      variantId: variant.id,
      variantName: variant.name,
      mrp: variant.mrp,
      sellingPrice: variant.price,
      maxOrderQuantity: variant.maxOrderQuantity ?? null,
      isAvailable: variant.isAvailable,
    });
  }

  closeVariantEditor(): void {
    if (this.variantSaving()) return;
    this.variantEditDraft.set(null);
  }

  async saveVariantEdit(): Promise<void> {
    const draft = this.variantEditDraft();
    if (!draft || this.variantSaving()) return;
    if (!this.selectedStoreId()) {
      this.snackbar.warning('Select a store to edit');
      return;
    }

    const sellingPrice = Number(draft.sellingPrice);
    if (!Number.isFinite(sellingPrice) || sellingPrice < 0) {
      this.snackbar.error('Enter a valid selling price');
      return;
    }

    const limit = draft.maxOrderQuantity == null ? null : Number(draft.maxOrderQuantity);
    if (limit !== null && (!Number.isInteger(limit) || limit < 1 || limit > 2147483647)) {
      this.snackbar.error('Maximum units must be a positive whole number, or blank for no limit');
      return;
    }
    this.variantSaving.set(true);

    this.StoreProductService.updateStoreProductById(draft.storeProductId, {
      price: sellingPrice,
      max_order_quantity: limit,
      is_available: !!draft.isAvailable,
    }).subscribe({
      next: () => {
        this.snackbar.success('Store price, availability and purchase limit updated');
        this.variantSaving.set(false);
        this.variantEditDraft.set(null);
        this.refreshInventory();
      },
      error: (err) => {
        console.error('Failed to update selling price', err);
        this.snackbar.error('Failed to update selling price');
        this.variantSaving.set(false);
      },
    });
  }

  requestRemoveVariantMapping(product: StoreProduct, variant: Variant): void {
    if (this.variantRemoveSaving()) return;
    if (!this.selectedStoreId()) {
      this.snackbar.warning('Select a store to remove mapping');
      return;
    }

    if (!this.authService.isSuperAdmin()) {
      this.snackbar.warning('Only a superadmin can remove store mappings. Use Edit settings to turn off availability.');
      return;
    }
    if (product.storeId !== this.selectedStoreId()) {
      this.snackbar.warning('The selected store changed. Refresh inventory before removing this mapping.');
      return;
    }
    if (variant.stock !== 0) {
      this.snackbar.warning('Stock remains. Use Update stock with a reason to reconcile it to zero, or turn off Available in store in Edit settings.');
      return;
    }

    this.variantRemoveError.set('');
    this.variantRemoveDraft.set({
      storeId: product.storeId,
      storeProductId: variant.storeProductId,
      productId: product.id,
      productName: product.name,
      variantId: variant.id,
      variantName: variant.name,
    });
  }

  closeRemoveVariantModal(): void {
    if (this.variantRemoveSaving()) return;
    this.variantRemoveDraft.set(null);
  }

  confirmRemoveVariantMapping(): void {
    const draft = this.variantRemoveDraft();
    if (!draft || this.variantRemoveSaving()) return;
    if (draft.storeId !== this.selectedStoreId() || !this.authService.isSuperAdmin()) {
      this.variantRemoveError.set('Store or permission changed. Close this dialog and refresh inventory.');
      return;
    }

    this.variantRemoveError.set('');
    this.variantRemoveSaving.set(true);

    this.StoreProductService.deleteStoreProductById(
      draft.storeProductId, draft.storeId
    ).subscribe({
      next: () => {
        this.snackbar.success('Variant removed from store');
        this.variantRemoveSaving.set(false);
        this.variantRemoveDraft.set(null);
        this.loadStoreProducts();
      },
      error: (err) => {
        console.error('Failed to remove mapping', err);
        const message = err instanceof Error ? err.message : 'Could not remove this store mapping. Refresh inventory and try again.';
        this.variantRemoveError.set(message);
        this.snackbar.error(message);
        this.variantRemoveSaving.set(false);
      },
    });
  }

  setStockEditMode(mode: StockEditMode): void {
    const draft = this.stockEditDraft();
    if (!draft || this.stockSaving()) return;
    this.stockEditDraft.set({ ...draft, mode });
  }

  setStockEditQuantity(quantity: unknown): void {
    const draft = this.stockEditDraft();
    if (!draft || this.stockSaving()) return;

    const numericQuantity = Number(quantity);
    this.stockEditDraft.set({
      ...draft,
      quantity: Number.isFinite(numericQuantity) ? numericQuantity : 0,
    });
  }

  setVariantSellingPrice(price: unknown): void {
    const draft = this.variantEditDraft();
    if (!draft || this.variantSaving()) return;
    const numericPrice = Number(price);
    this.variantEditDraft.set({
      ...draft,
      sellingPrice: Number.isFinite(numericPrice)
        ? numericPrice
        : draft.sellingPrice,
    });
  }

  setStockEditThreshold(value: unknown): void {
    const draft = this.stockEditDraft();
    if (!draft || this.stockSaving()) return;
    const numeric = Number(value);
    this.stockEditDraft.set({
      ...draft,
      lowStockThreshold: Number.isFinite(numeric) ? numeric : draft.lowStockThreshold,
    });
  }

  setStockEditAvailable(isAvailable: unknown): void {
    const draft = this.stockEditDraft();
    if (!draft || this.stockSaving()) return;
    this.stockEditDraft.set({ ...draft, isAvailable: !!isAvailable });
  }

  setVariantMaxOrderQuantity(value: number | null): void {
    const draft = this.variantEditDraft();
    if (draft && !this.variantSaving()) this.variantEditDraft.set({ ...draft, maxOrderQuantity: value });
  }

  setVariantAvailability(isAvailable: unknown): void {
    const draft = this.variantEditDraft();
    if (!draft || this.variantSaving()) return;
    this.variantEditDraft.set({ ...draft, isAvailable: !!isAvailable });
  }

  async saveStockEdit(): Promise<void> {
    if (this.stockChangeReason.trim().length < 5) { this.snackbar.error('Explain the stock change (at least five characters)'); return; }
    const draft = this.stockEditDraft();
    if (!draft) return;

    const quantity = Number(draft.quantity);
    if (!Number.isFinite(quantity) || quantity < 0) {
      this.snackbar.error('Enter a valid non-negative quantity');
      return;
    }

    const newStock =
      draft.mode === 'set'
        ? Math.floor(quantity)
        : Math.floor(draft.currentStock + quantity);

    if (newStock < 0) {
      this.snackbar.error('Stock cannot be negative');
      return;
    }

    this.stockSaving.set(true);
    this.StoreProductService.mutateStoreProduct({
      storeProductId: draft.storeProductId,
      reason: this.stockChangeReason,
      setStock: draft.mode === 'set' ? Math.floor(quantity) : undefined,
      addStock: draft.mode === 'add' ? Math.floor(quantity) : undefined,
      lowStockThreshold: Math.floor(Number(draft.lowStockThreshold) || 0),
      isAvailable: draft.isAvailable,
    }).subscribe({
      next: (updated) => {
        this.storeProducts.update((products) =>
          products.map((p) => {
            if (p.id !== draft.productId) return p;
            return {
              ...p,
              variants: p.variants.map((v) => {
                if (v.storeProductId !== draft.storeProductId) return v;
                const isAvailable = updated.is_available;
                const stock = updated.stock;
                const threshold =
                  updated.low_stock_threshold ?? draft.lowStockThreshold;
                return {
                  ...v,
                  stock,
                  isAvailable,
                  lowStockThreshold: threshold,
                  status: this.getStatusFromStock(stock, isAvailable, threshold),
                };
              }),
            };
          })
        );

        this.snackbar.success('Stock updated');
        this.stockSaving.set(false);
        this.stockEditDraft.set(null);
        // Keep UI consistent with backend calculations / policies (RLS, triggers, etc.)
        this.refreshInventory();
      },
      error: (err) => {
        console.error('Failed to update stock', err);
        this.snackbar.error('Failed to update stock');
        this.stockSaving.set(false);
      },
    });
  }

  getVariantTone(status: Variant['status']): string {
    switch (status) {
      case 'IN STOCK':
        return 'badge-success';
      case 'LOW STOCK':
        return 'badge-warning';
      case 'UNAVAILABLE':
        return 'badge-ghost';
      default:
        return 'badge-error';
    }
  }

  countVariantsByStatus(product: StoreProduct, status: VariantStatus): number {
    return product.variants.filter((v) => v.status === status).length;
  }

  productStock(product: StoreProduct): number {
    return product.variants.reduce((total, variant) => total + variant.stock, 0);
  }

  getStockPercentage(product: StoreProduct): number {
    const total = product.variants.reduce((sum, v) => sum + v.stock, 0);
    const maxExpected = product.variants.length * 100;
    return Math.min(100, Math.round((total / maxExpected) * 100));
  }

  getHealthStatus(product: StoreProduct): {
    label: string;
    class: string;
    icon: string;
  } {
    // console.log('Evaluating health status for product', product);
    const outCount = this.countVariantsByStatus(product, 'OUT OF STOCK');
    const lowCount = this.countVariantsByStatus(product, 'LOW STOCK');
    const total = product.variants.length;

    if (outCount === total) {
      return {
        label: 'Critical',
        class: 'text-error',
        icon: 'fa-solid fa-circle-exclamation',
      };
    }
    if (outCount > 0 || lowCount > total * 0.5) {
      return {
        label: 'At Risk',
        class: 'text-warning',
        icon: 'fa-solid fa-triangle-exclamation',
      };
    }
    if (lowCount > 0) {
      return {
        label: 'Moderate',
        class: 'text-info',
        icon: 'fa-solid fa-circle-info',
      };
    }
    return {
      label: 'Healthy',
      class: 'text-success',
      icon: 'fa-solid fa-check-circle',
    };
  }

  trackByProductId(index: number, product: StoreProduct): string {
    return product.groupKey;
  }

  trackByVariantId(index: number, variant: any) {
    return `${variant.id}-${index}`;
  }

  getVisiblePages(): (number | -1)[] {
    const total = this.totalPages();
    const current = this.currentPage();
    const pages: (number | -1)[] = [];

    if (total <= 7) {
      for (let i = 1; i <= total; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      if (current > 3) {
        pages.push(-1);
      }

      const start = Math.max(2, current - 1);
      const end = Math.min(total - 1, current + 1);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (current < total - 2) {
        pages.push(-1);
      }

      pages.push(total);
    }

    return pages;
  }
  editStoreProduct(product: StoreProduct): void {
    console.log('Edit product', product);
    // Navigate to edit page with product ID
    // You can use Angular's Router for navigation
    // For example:
    // this.router.navigate(['/inventory/store-products/edit', product.id]);
  }
}
